import React from 'react';
import { Heart, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export function QuickDonation() {
  const [copiedField, setCopiedField] = React.useState<'blik' | null>(null);

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField('blik');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      // Fallback: do nothing if copy fails
    }
  };

  return (
    <section className="py-16 px-8 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
              </div>
              <h2 className="heading-2 mb-0">
                Wpłać teraz i pomóż
              </h2>
            </div>
            
            <p className="body-large mb-8">
              Twoja darowizna pomoże nam zapewnić opiekę, leczenie i bezpieczny dom dla bezdomnych zwierząt.
              Do czasu uruchomienia bramki płatniczej możesz wysłać przelew BLIK na nasz numer telefonu.
            </p>

            <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black border border-accessible-gray-200 dark:border-accessible-gray-700 rounded-lg p-6 mb-6 shadow-sm">
              <div className="flex items-center justify-between gap-6 mb-3">
                <p className="label-large mb-0">BLIK na telefon</p>
                <img
                  src="/images/payments/BLIK/blik.svg"
                  alt="BLIK"
                  className="h-10 sm:h-12 md:h-14 w-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="max-w-md">
                <div className="flex items-center border border-accessible-gray-300 dark:border-accessible-gray-600 high-contrast:border-white high-contrast:border-2 rounded-lg overflow-hidden">
                  <div className="flex-1 px-4 py-3 bg-white dark:bg-accessible-gray-700 high-contrast:bg-black">
                    <span className="font-mono text-2xl leading-none select-all text-accessible-gray-900 dark:text-white high-contrast:text-white">664 821 160</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard('664821160')}
                    className="h-full inline-flex items-center gap-2 px-4 py-3 text-2xl leading-none bg-accessible-gray-50 dark:bg-accessible-gray-600 hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-500 text-accessible-gray-900 dark:text-white high-contrast:bg-white high-contrast:text-black high-contrast:hover:bg-white border-l border-accessible-gray-300 dark:border-accessible-gray-600 high-contrast:border-white transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accessible-red-600"
                    aria-label={copiedField === 'blik' ? 'Skopiowano numer BLIK' : 'Skopiuj numer BLIK'}
                  >
                    {copiedField === 'blik' ? (
                      <>
                        <Check className="w-6 h-6" aria-hidden="true" />
                        <span>Skopiowano</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-6 h-6" aria-hidden="true" />
                        <span>Kopiuj</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="body-small text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white mt-3 mb-0">
                Otwórz aplikację banku → BLIK → Przelew na telefon → wklej numer i zatwierdź.
              </p>
            </div>

            <Link
              to="/wplata-jednorazowa"
              className="w-full bg-accessible-red-600 text-white py-4 rounded-lg hover:bg-accessible-red-700 transition-colors flex items-center justify-center gap-2 btn-text-large"
            >
              <span>ZOBACZ INNE METODY WSPARCIA</span>
            </Link>
          </div>
          
          <div className="relative">
            <div className="aspect-square rounded-xl overflow-hidden shadow-2xl">
              <img 
                src="/images/animals/fundacja-znajdki-zwierzeta-005.webp" 
                alt="Kotki z Fundacji Znajdki" 
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 hidden xl:block">
                <div className="bg-white/90 dark:bg-accessible-gray-800/90 high-contrast:bg-white hc-on-white p-6 rounded-lg">
                  <h3 className="heading-4 mb-3 high-contrast:text-black">
                    Dlaczego Twoja pomoc jest ważna?
                  </h3>
                  <p className="body-medium mb-0 high-contrast:text-black">
                    Każdego miesiąca ratujemy kilkadziesiąt zwierząt. Bez Twojego wsparcia nie byłoby to możliwe. 
                    Dołącz do grona naszych darczyńców i pomóż nam kontynuować naszą misję.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="absolute -top-6 -right-6 bg-accessible-red-600 text-white p-4 rounded-full shadow-lg transform rotate-12">
              <div className="text-center">
                <span className="block text-sm font-bold">PILNE</span>
                <span className="block text-xl font-bold">POTRZEBY</span>
              </div>
            </div>
          </div>

          {/* Text block under the image for mobile and tablets */}
          <div className="xl:hidden mt-4">
            <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-white hc-on-white p-6 rounded-lg shadow-sm">
              <h3 className="heading-4 mb-3 high-contrast:text-black">
                Dlaczego Twoja pomoc jest ważna?
              </h3>
              <p className="body-medium mb-0 high-contrast:text-black">
                Każdego miesiąca ratujemy kilkadziesiąt zwierząt. Bez Twojego wsparcia nie byłoby to możliwe. 
                Dołącz do grona naszych darczyńców i pomóż nam kontynuować naszą misję.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}